import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, FileText, X } from 'lucide-react';
import Calendar from '../../../core/components/dashboard/Calendar/Calendar';
import { getCouponService } from '../../../domains/coupon';
import useAuthStore from '../../../core/stores/authStore';
import useUIStore from '../../../core/stores/uiStore';
import styles from './CouponDistributions.module.css';

const DISCOUNT_TYPE_CONFIG = {
  FIXED: { label: '고정금액', color: '#2196F3', bg: '#e3f2fd' },
  PERCENT: { label: '비율할인', color: '#4CAF50', bg: '#e8f5e9' },
};

const DISCOUNT_TYPE_LABELS = {
  FIXED: '고정금액',
  PERCENT: '비율할인',
};

const SCOPE_TYPE_LABELS = {
  PHARMACY: '우리 약국',
  PHARM_CODE: '지역 그룹',
  BRANCH_CODE: '브랜치 그룹',
  ALL: '전체',
};

const GENDER_LABELS = {
  ALL: '전체',
  MALE: '남성',
  FEMALE: '여성',
};

const TYPE_TABS = [
  { key: 'all', label: '전체' },
  { key: 'FIXED', label: '고정금액' },
  { key: 'PERCENT', label: '비율할인' },
];

const PERIOD_OPTIONS = [
  { key: 'yesterday', label: '어제' },
  { key: 'lastWeek', label: '지난주' },
  { key: 'lastMonth', label: '지난달' },
  { key: 'thisMonth', label: '이번 달' },
];

const ITEMS_PER_PAGE = 10;

const fmtDate = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDateRangeForPeriod = key => {
  const today = new Date();
  let start, end;
  switch (key) {
    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      start = d;
      end = new Date(d);
      break;
    }
    case 'lastWeek': {
      const d = new Date(today);
      const dayOfWeek = d.getDay();
      end = new Date(d);
      end.setDate(d.getDate() - dayOfWeek - 1);
      start = new Date(end);
      start.setDate(end.getDate() - 6);
      break;
    }
    case 'lastMonth': {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    }
    case 'thisMonth': {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      break;
    }
    default: {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    }
  }
  return { start, end };
};

const formatTargetAges = (ages) => {
  if (!ages || ages.length === 0) return '전체';
  return ages.map(a => `${a}대`).join(', ');
};

const getTargetDisplay = (coupon) => {
  const gender = GENDER_LABELS[coupon.target_gender] || '전체';
  const ages = formatTargetAges(coupon.target_ages);
  if (gender === '전체' && ages === '전체') return '전체';
  return `${gender} / ${ages}`;
};

const CouponDistributions = () => {
  const navigate = useNavigate();
  const pharmacy = useAuthStore(state => state.pharmacy);
  const setHeaderTabs = useUIStore(state => state.setHeaderTabs);

  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [periodFilter, setPeriodFilter] = useState('thisMonth');
  const [dateRange, setDateRange] = useState(() => getDateRangeForPeriod('thisMonth'));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const calendarRef = useRef(null);

  // Header tabs
  useEffect(() => {
    setHeaderTabs(
      [
        { id: 'coupon', label: '쿠폰' },
        { id: 'point', label: '포인트' },
      ],
      'coupon',
      (id) => {
        if (id === 'point') navigate('/dashboard/points/distributions');
      }
    );
    return () => setHeaderTabs(null);
  }, [setHeaderTabs, navigate]);

  const refreshCoupons = useCallback(async () => {
    try {
      setIsLoading(true);
      const couponService = getCouponService();
      const response = await couponService.getCouponsByPharmacyId(pharmacy?.id, {
        limit: 100,
        offset: 0,
      });
      if (response?.result_code === '200' && response.data) {
        const items = response.data.items || [];
        setCoupons(items);
        setTotalCount(response.data.total || items.length);
      }
    } catch (error) {
      console.error('쿠폰 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [pharmacy?.id]);

  useEffect(() => {
    if (pharmacy?.id) {
      refreshCoupons();
    }
  }, [refreshCoupons, pharmacy?.id]);

  // Calendar outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  // KPI counts
  const kpi = useMemo(() => ({
    total: coupons.length,
    fixed: coupons.filter(c => c.discount_type === 'FIXED').length,
    percent: coupons.filter(c => c.discount_type === 'PERCENT').length,
  }), [coupons]);

  // Filtered data
  const filteredCoupons = useMemo(() => {
    let filtered = [...coupons];

    if (activeTab !== 'all') {
      filtered = filtered.filter(c => c.discount_type === activeTab);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        (c.description || '').toLowerCase().includes(q) ||
        getTargetDisplay(c).toLowerCase().includes(q)
      );
    }

    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(c => {
        const dateStr = c.created_at || c.expiry_date;
        if (!dateStr) return true;
        const date = new Date(dateStr);
        return date >= dateRange.start && date <= new Date(dateRange.end.getTime() + 86400000);
      });
    }

    return filtered;
  }, [coupons, activeTab, searchQuery, dateRange]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredCoupons.length / ITEMS_PER_PAGE));
  const paginatedCoupons = filteredCoupons.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, periodFilter, dateRange]);

  const formatCouponAmount = (coupon) => {
    if (coupon.discount_type === 'PERCENT') return `${coupon.amount}%`;
    return `${Number(coupon.amount || 0).toLocaleString()}원`;
  };

  const handlePeriodChange = key => {
    setPeriodFilter(key);
    setDateRange(getDateRangeForPeriod(key));
  };

  const handleCalendarChange = val => {
    if (val.start && val.end) {
      setPeriodFilter('');
      setDateRange({ start: val.start, end: val.end });
      setIsCalendarOpen(false);
    } else if (val.start) {
      setDateRange(prev => ({ ...prev, start: val.start }));
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className={styles.pageContainer}>
      {/* Summary Cards */}
      <div className={styles.summaryRow}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>전체</div>
          <div className={styles.summaryValue}>{kpi.total}건</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>고정금액</div>
          <div className={styles.summaryValue}>{kpi.fixed}건</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>퍼센트 할인</div>
          <div className={styles.summaryValue}>{kpi.percent}건</div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className={styles.mainCard}>
        {/* Search Bar */}
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrap}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="설명, 대상으로 검색"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <button className={styles.searchButton} onClick={handleSearch}>
            검색
          </button>
          <div className={styles.searchHint}>
            <Info className={styles.searchHintIcon} />
            <span>조회기간 내 데이터만 검색됩니다</span>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filterArea}>
          {/* Date range + period pills */}
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>조회기간</span>
            <div className={styles.dateRangeWrapper} ref={isCalendarOpen ? calendarRef : null}>
              <div className={styles.dateInputGroup} onClick={() => setIsCalendarOpen(prev => !prev)}>
                <span className={styles.dateDisplay}>{fmtDate(dateRange.start)}</span>
                <span className={styles.dateArrow}>~</span>
                <span className={styles.dateDisplay}>{fmtDate(dateRange.end)}</span>
              </div>
              {isCalendarOpen && (
                <div className={styles.calendarDropdown}>
                  <Calendar
                    mode="range"
                    value={dateRange}
                    onChange={handleCalendarChange}
                  />
                </div>
              )}
            </div>
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`${styles.pill} ${periodFilter === opt.key ? styles.pillActive : ''}`}
                onClick={() => handlePeriodChange(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Vertical divider */}
          <div className={styles.filterDivider} />

          {/* Type pills */}
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>유형</span>
            {TYPE_TABS.map(tab => (
              <button
                key={tab.key}
                className={`${styles.pill} ${activeTab === tab.key ? styles.pillActive : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableBox}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>쿠폰 ID</th>
                <th>타겟 대상</th>
                <th>할인 방식</th>
                <th>금액/비율</th>
                <th>범위</th>
                <th>설명</th>
                <th>만료일</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className={styles.emptyState}>
                    <div className={styles.emptyText}>로딩 중...</div>
                  </td>
                </tr>
              ) : paginatedCoupons.length > 0 ? (
                paginatedCoupons.map((coupon, idx) => {
                  const dtConfig = DISCOUNT_TYPE_CONFIG[coupon.discount_type] || DISCOUNT_TYPE_CONFIG.FIXED;
                  return (
                    <tr
                      key={coupon.coupon_id || coupon.id || idx}
                      className={styles.clickableRow}
                      onClick={() => setSelectedCoupon(coupon)}
                    >
                      <td className={styles.couponId}>#{coupon.coupon_id || coupon.id || '-'}</td>
                      <td>{getTargetDisplay(coupon)}</td>
                      <td>
                        <span
                          className={styles.typeBadge}
                          style={{ backgroundColor: dtConfig.bg, color: dtConfig.color }}
                        >
                          {dtConfig.label}
                        </span>
                      </td>
                      <td className={styles.amount}>{formatCouponAmount(coupon)}</td>
                      <td>{SCOPE_TYPE_LABELS[coupon.scope_type] || coupon.scope_type || '-'}</td>
                      <td>{coupon.description || '-'}</td>
                      <td>{coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString('ko-KR') : '-'}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className={styles.emptyState}>
                    <FileText className={styles.emptyIcon} />
                    <div className={styles.emptyText}>지급된 쿠폰이 없습니다</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles.paginationBar}>
          <div className={styles.paginationInfo}>
            검색결과 {filteredCoupons.length}개
          </div>
          <div className={styles.paginationButtons}>
            <button
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              &#8249;
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`${styles.pageBtn} ${currentPage === pageNum ? styles.activePage : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              &#8250;
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedCoupon && (
        <div className={styles.modalOverlay} onClick={() => setSelectedCoupon(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>쿠폰 상세</h2>
              <button className={styles.closeButton} onClick={() => setSelectedCoupon(null)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>쿠폰 ID</div>
                <div className={styles.detailValue}>#{selectedCoupon.coupon_id || selectedCoupon.id || '-'}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>바코드</div>
                <div className={styles.detailValue}>{selectedCoupon.barcode || '-'}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>할인 방식</div>
                <div className={styles.detailValue}>{DISCOUNT_TYPE_LABELS[selectedCoupon.discount_type] || '-'}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>금액/비율</div>
                <div className={styles.detailValue}>{formatCouponAmount(selectedCoupon)}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>사용 범위</div>
                <div className={styles.detailValue}>{SCOPE_TYPE_LABELS[selectedCoupon.scope_type] || '-'}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>만료일</div>
                <div className={styles.detailValue}>
                  {selectedCoupon.expiry_date ? new Date(selectedCoupon.expiry_date).toLocaleDateString('ko-KR') : '-'}
                </div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>타겟 성별</div>
                <div className={styles.detailValue}>
                  {GENDER_LABELS[selectedCoupon.target_gender] || '전체'}
                </div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>타겟 연령대</div>
                <div className={styles.detailValue}>
                  {formatTargetAges(selectedCoupon.target_ages)}
                </div>
              </div>
              {selectedCoupon.description && (
                <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                  <div className={styles.detailLabel}>설명</div>
                  <div className={styles.detailValue}>{selectedCoupon.description}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponDistributions;
